/* Sticky add-to-cart bar — Downsized Designs (built 2026-10-22;
 * submit path fixed 2026-10-22, see "The add itself" below).
 *
 * The bar rendered by snippets/sticky-atc-bar.liquid has no form of its
 * own. Its button activates the page's real Add to Cart button, so
 * variant, quantity, cart drawer, error handling, and form-attached
 * validation (e.g. Easify's required personalization check) are
 * identical to using the main button.
 *
 * Displayed state is mirrored from the theme's own machinery:
 *  - primary: the theme's pub/sub 'variant-change' event (published by
 *    product-info.js after it has swapped price, media, and inputs);
 *  - fallback: MutationObservers on the [data-selected-variant] JSON the
 *    theme keeps inside <variant-selects>, and on the main submit button
 *    (disabled / aria-disabled / label), so the bar can never show a
 *    stale variant, a stale price, or an enabled button for a sold-out
 *    or unavailable variant.
 *
 * Visibility: shown only while the main buy button is scrolled out of
 * view; hidden again when it returns, when the footer arrives, and while
 * the cart drawer is open.
 */
(function () {
  if (window.__ddStickyAtcLoaded) return;
  window.__ddStickyAtcLoaded = true;

  function formatPrice(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency,
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function variantImageSrc(variant) {
    if (!variant) return null;
    var src = null;
    if (variant.featured_image && variant.featured_image.src) src = variant.featured_image.src;
    else if (variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src)
      src = variant.featured_media.preview_image.src;
    else if (variant.featured_media && variant.featured_media.src) src = variant.featured_media.src;
    if (!src) return null;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=120';
  }

  function initBar(bar) {
    if (bar.dataset.stickyAtcInit) return;
    bar.dataset.stickyAtcInit = 'true';

    var sectionId = bar.dataset.sectionId;
    var productInfo = bar.closest('product-info');
    if (!productInfo) return;

    var mainButton = productInfo.querySelector('#ProductSubmitButton-' + sectionId);
    if (!mainButton) return; // No real buy button on this page: never show the bar.

    var variantEl = bar.querySelector('[data-sticky-atc-variant]');
    var priceEl = bar.querySelector('[data-sticky-atc-price]');
    var imageEl = bar.querySelector('[data-sticky-atc-image]');
    var barButton = bar.querySelector('[data-sticky-atc-button]');
    var barButtonText = bar.querySelector('[data-sticky-atc-button-text]');

    function syncButtonState() {
      var mainText = mainButton.querySelector('span');
      var label = mainText ? mainText.textContent.trim() : mainButton.textContent.trim();
      if (barButtonText && label && barButtonText.textContent !== label) barButtonText.textContent = label;
      var disabled =
        mainButton.hasAttribute('disabled') || mainButton.getAttribute('aria-disabled') === 'true';
      if (disabled) barButton.setAttribute('disabled', 'disabled');
      else barButton.removeAttribute('disabled');
    }

    function syncDisplay(variant) {
      if (variant) {
        var title = variant.title || '';
        if (variantEl && variantEl.textContent !== title) variantEl.textContent = title;
        var price = formatPrice(variant.price);
        if (priceEl && price && priceEl.textContent !== price) priceEl.textContent = price;
        var src = variantImageSrc(variant);
        if (imageEl && src && imageEl.getAttribute('src') !== src) imageEl.setAttribute('src', src);
      }
      syncButtonState();
    }

    function readDomVariant() {
      var holder = productInfo.querySelector('variant-selects [data-selected-variant]');
      if (!holder) return null;
      try {
        return JSON.parse(holder.textContent);
      } catch (e) {
        return null;
      }
    }

    var domSyncTimer = null;
    function scheduleDomSync() {
      if (domSyncTimer) return;
      domSyncTimer = setTimeout(function () {
        domSyncTimer = null;
        syncDisplay(readDomVariant());
      }, 60);
    }

    // Primary sync: the theme's own variant-change event.
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, function (event) {
        if (event && event.data && event.data.sectionId === sectionId) syncDisplay(event.data.variant);
      });
    }

    // Fallback sync: watch the theme's selected-variant JSON and the main
    // button itself, so state can never drift even if the event is missed.
    // product-info.js swaps the whole <variant-selects> subtree on a
    // variant change, so the holder node is re-bound whenever it is
    // replaced.
    var holderObserver = new MutationObserver(scheduleDomSync);
    var observedHolder = null;
    function bindHolder() {
      var holder = productInfo.querySelector('variant-selects [data-selected-variant]');
      if (holder && holder !== observedHolder) {
        holderObserver.disconnect();
        observedHolder = holder;
        holderObserver.observe(holder, { childList: true, characterData: true, subtree: true });
        scheduleDomSync();
      }
    }
    bindHolder();
    new MutationObserver(bindHolder).observe(productInfo, { childList: true, subtree: true });
    new MutationObserver(syncButtonState).observe(mainButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
      childList: true,
      characterData: true,
      subtree: true,
    });

    // The add itself: activate the page's real Add to Cart button the
    // way a genuine press does, so whatever validation the form carries
    // (including Easify's required-field check) runs exactly as it does
    // for the main button.
    //
    // Why not just mainButton.click()? (submit-path fix, 2026-10-22)
    // A bare synthetic .click() is ONLY a click event: no pointerdown /
    // mousedown before it, no focus change (so the personalization
    // field never blurs and nothing on the page registers a real press
    // on the button). Verified live against the real Easify script:
    // with the required field EMPTY, Easify's error still fired through
    // the bare click, but with the field FILLED the add silently never
    // completed (three attempts), while the main button with the same
    // input added instantly. Canyon's own code cannot be the
    // discriminator — product-form.js listens only for the form's
    // submit event — so Easify's validate-and-release flow depends on
    // the genuine press sequence around the click, which .click() does
    // not reproduce. The fix therefore:
    //   1. replays the full press on the real button (pointerdown →
    //      mousedown → focus → pointerup → mouseup → native .click()),
    //      which also blurs/commits the personalization field exactly
    //      as a genuine press does; then
    //   2. if the submission demonstrably never starts (the button
    //      never enters its loading state, no cart update/error is
    //      published, the drawer never opens), falls back ONCE to the
    //      form's own submission entry point,
    //      form.requestSubmit(mainButton) — a user-agent-fired submit
    //      event with the real button as submitter, which still passes
    //      through the form's submit-phase validation (Easify
    //      included). It is NOT a cart AJAX call and never bypasses the
    //      form. The fallback is suppressed whenever a required
    //      line-item-property field in the form is empty, so it can
    //      never push an item past a required-field guard; and
    //      product-form's own re-entrancy guard (it sets aria-disabled
    //      synchronously when a submit reaches it) makes an overlapping
    //      late release a no-op, so one tap can never add twice.
    var watchdogTimer = null;
    var watchdogCleanup = null;

    function cancelSubmitWatchdog() {
      if (watchdogTimer) {
        clearTimeout(watchdogTimer);
        watchdogTimer = null;
      }
      if (watchdogCleanup) {
        watchdogCleanup();
        watchdogCleanup = null;
      }
    }

    function requiredPropertyFieldEmpty() {
      var form = mainButton.form || productInfo.querySelector('form');
      if (!form) return false;
      var fields = form.querySelectorAll(
        'input[name^="properties["], textarea[name^="properties["], select[name^="properties["]'
      );
      for (var i = 0; i < fields.length; i++) {
        var field = fields[i];
        if (field.disabled || field.type === 'hidden') continue;
        var required =
          field.hasAttribute('required') || field.getAttribute('aria-required') === 'true';
        if (required && !String(field.value || '').trim()) return true;
      }
      return false;
    }

    function submissionStarted() {
      // Synchronous evidence that a submit reached product-form's
      // handler (it sets both at handler start). The cart drawer is
      // deliberately NOT checked here: it opens only after the add's
      // fetch resolves, and a drawer already open from an earlier add
      // would say nothing about THIS tap — the watchdog below latches
      // the drawer by transition instead.
      if (mainButton.classList.contains('loading')) return true;
      if (mainButton.getAttribute('aria-disabled') === 'true') return true;
      return false;
    }

    function armSubmitWatchdog() {
      cancelSubmitWatchdog();
      var started = submissionStarted();
      function mark() {
        started = true;
      }
      var observers = [];
      var buttonObserver = new MutationObserver(function () {
        if (submissionStarted()) mark();
      });
      buttonObserver.observe(mainButton, {
        attributes: true,
        attributeFilter: ['class', 'aria-disabled'],
      });
      observers.push(buttonObserver);
      var drawer = document.querySelector('cart-drawer');
      if (drawer) {
        var drawerObserver = new MutationObserver(function () {
          if (drawer.classList.contains('active')) mark();
        });
        drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        observers.push(drawerObserver);
      }
      var unsubscribes = [];
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        [window.PUB_SUB_EVENTS.cartUpdate, window.PUB_SUB_EVENTS.cartError].forEach(function (name) {
          if (name) unsubscribes.push(window.subscribe(name, mark));
        });
      }
      watchdogCleanup = function () {
        observers.forEach(function (observer) {
          observer.disconnect();
        });
        unsubscribes.forEach(function (unsubscribe) {
          try {
            unsubscribe();
          } catch (e) {}
        });
      };
      watchdogTimer = setTimeout(function () {
        watchdogTimer = null;
        var cleanup = watchdogCleanup;
        watchdogCleanup = null;
        if (cleanup) cleanup();
        if (started || submissionStarted()) return;
        // A required personalization field is still empty: "blocked" is
        // the correct outcome (Easify shows its error), never bypass it.
        if (requiredPropertyFieldEmpty()) return;
        var form = mainButton.form || productInfo.querySelector('form');
        if (form && typeof form.requestSubmit === 'function') {
          try {
            form.requestSubmit(mainButton);
            return;
          } catch (e) {}
        }
        mainButton.click();
      }, 900);
    }

    function pressMainButton(sourceEvent) {
      var pointerType = 'mouse';
      var pointerId = 1;
      if (sourceEvent) {
        if (sourceEvent.pointerType) pointerType = sourceEvent.pointerType;
        if (sourceEvent.pointerId) pointerId = sourceEvent.pointerId;
      }
      function dispatchPointer(type, buttons) {
        if (typeof window.PointerEvent !== 'function') return;
        try {
          mainButton.dispatchEvent(
            new PointerEvent(type, {
              bubbles: true,
              composed: true,
              cancelable: true,
              view: window,
              pointerId: pointerId,
              pointerType: pointerType,
              isPrimary: true,
              button: 0,
              buttons: buttons,
            })
          );
        } catch (e) {}
      }
      function dispatchMouse(type, buttons) {
        try {
          mainButton.dispatchEvent(
            new MouseEvent(type, {
              bubbles: true,
              composed: true,
              cancelable: true,
              view: window,
              detail: 1,
              button: 0,
              buttons: buttons,
            })
          );
        } catch (e) {}
      }
      dispatchPointer('pointerdown', 1);
      dispatchMouse('mousedown', 1);
      // A genuine press moves focus to the button (blurring, and so
      // committing, whatever field the buyer was in). preventScroll
      // keeps the page where the buyer left it — the button is
      // off-screen by design whenever the bar is visible.
      try {
        mainButton.focus({ preventScroll: true });
      } catch (e) {}
      dispatchPointer('pointerup', 0);
      dispatchMouse('mouseup', 0);
      mainButton.click();
    }

    barButton.addEventListener('click', function (event) {
      if (barButton.hasAttribute('disabled')) return;
      if (mainButton.hasAttribute('disabled')) return;
      if (mainButton.getAttribute('aria-disabled') === 'true') return;
      cancelSubmitWatchdog();
      pressMainButton(event);
      armSubmitWatchdog();
    });

    // ---- Visibility ----------------------------------------------------
    var buyButtonVisible = true;
    var footerVisible = false;
    var drawerOpen = false;

    function updateVisibility() {
      var show = !buyButtonVisible && !footerVisible && !drawerOpen;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          buyButtonVisible = entries[0].isIntersecting;
          updateVisibility();
        },
        { threshold: 0 }
      ).observe(mainButton);

      var footer = document.querySelector('footer');
      if (footer) {
        new IntersectionObserver(
          function (entries) {
            footerVisible = entries[0].isIntersecting;
            updateVisibility();
          },
          { threshold: 0 }
        ).observe(footer);
      }
    }

    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      var checkDrawer = function () {
        drawerOpen = cartDrawer.classList.contains('active');
        updateVisibility();
      };
      new MutationObserver(checkDrawer).observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class'],
      });
      checkDrawer();
    }

    syncButtonState();
    updateVisibility();
  }

  function boot() {
    document.querySelectorAll('[data-sticky-atc]').forEach(initBar);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
